<template>
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="sm:flex sm:items-center mb-8">
      <div class="sm:flex-auto">
        <h1 class="text-base font-semibold leading-6 text-gray-900">Saved Jobs</h1>
        <p class="mt-2 text-sm text-gray-700">
          Manage all the jobs you've saved for future reference.
        </p>
      </div>
      <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <a
          href="/jobs"
          class="bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Browse More Jobs
        </a>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="mb-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label for="search" class="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              v-model="filters.search"
              type="text"
              id="search"
              placeholder="Job title, company, or description"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label for="dateRange" class="block text-sm font-medium text-gray-700 mb-2">Saved Date</label>
            <select
              v-model="filters.dateRange"
              id="dateRange"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
          <div class="flex items-end">
            <button
              @click="applyFilters"
              class="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Actions -->
    <div v-if="selectedJobs.length > 0" class="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <span class="text-sm font-medium text-blue-900">
            {{ selectedJobs.length }} job{{ selectedJobs.length === 1 ? '' : 's' }} selected
          </span>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="removeSelectedJobs"
            class="bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm"
          >
            Remove Selected
          </button>
          <button
            @click="clearSelection"
            class="text-blue-600 hover:text-blue-800 text-sm"
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>

    <!-- Saved Jobs Grid -->
    <div v-if="isLoading" class="text-center py-8">
      <div class="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm text-white bg-red-900 rounded-md">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading...
      </div>
    </div>

    <div v-else-if="errorMessage" class="text-center py-8">
      <div class="text-red-600">{{ errorMessage }}</div>
      <button
        @click="fetchSavedJobs"
        class="mt-4 bg-red-900 text-white px-4 py-2 rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
      >
        Try Again
      </button>
    </div>

    <div v-else-if="filteredSavedJobs.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No saved jobs found</h3>
      <p class="mt-1 text-sm text-gray-500">Try adjusting your filters or save some interesting jobs!</p>
      <div class="mt-6">
        <a
          href="/jobs"
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-900 hover:bg-red-800"
        >
          Browse Jobs
        </a>
      </div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="job in filteredSavedJobs"
        :key="job.id"
        class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
      >
        <!-- Job Header -->
        <div class="p-6">
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ job.title }}</h3>
              <p class="text-gray-600 mb-1">{{ job.company }}</p>
              <p class="text-sm text-gray-500">{{ job.location }}</p>
            </div>
            <div class="flex items-center space-x-2">
              <input
                type="checkbox"
                :value="job.id"
                v-model="selectedJobs"
                class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <button
                @click="removeSavedJob(job.id)"
                class="text-red-600 hover:text-red-800"
                title="Remove from saved jobs"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Job Description -->
          <div class="mb-4">
            <p class="text-gray-700 text-sm line-clamp-3">{{ job.description }}</p>
          </div>

          <!-- Job Tags -->
          <div v-if="job.tags && job.tags.length > 0" class="mb-4">
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tag in job.tags.slice(0, 3)"
                :key="tag"
                class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                {{ tag }}
              </span>
              <span
                v-if="job.tags.length > 3"
                class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                +{{ job.tags.length - 3 }} more
              </span>
            </div>
          </div>

          <!-- Job Footer -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-200">
            <div class="text-sm text-gray-500">
              Saved {{ formatDate(job.savedDate) }}
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click="viewJob(job.id)"
                class="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View Job
              </button>
              <button
                v-if="job.link"
                @click="openJobLink(job.link)"
                class="text-green-600 hover:text-green-800 text-sm font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-8 flex items-center justify-between">
      <div class="flex items-center text-sm text-gray-700">
        <span>
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalItems) }} of {{ totalItems }} results
        </span>
      </div>
      <div class="flex items-center space-x-2">
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span class="px-3 py-2 text-sm font-medium text-gray-700">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

// Data
const isLoading = ref(true)
const errorMessage = ref('')
const savedJobs = ref([])
const selectedJobs = ref([])
const currentPage = ref(1)
const itemsPerPage = ref(12)
const totalItems = ref(0)

// Filters
const filters = ref({
  search: '',
  dateRange: ''
})

// Sample data (replace with API calls)
const sampleSavedJobs = [
  {
    id: 1,
    title: 'Senior Software Engineer',
    company: 'TechCorp International',
    location: 'Berlin, Germany',
    description: 'Looking for a senior developer to join our Berlin team. Remote work possible, competitive salary, great benefits. Experience with React, Node.js, and cloud platforms preferred.',
    tags: ['JavaScript', 'React', 'Node.js', 'Remote'],
    link: 'https://techcorp.com/careers/senior-engineer',
    savedDate: '2024-01-16'
  },
  {
    id: 2,
    title: 'Marketing Manager',
    company: 'Global Marketing Solutions',
    location: 'Amsterdam, Netherlands',
    description: 'Join our Amsterdam office as Marketing Manager. Lead international campaigns and manage a team of 5. Fluent English required, Dutch is a plus.',
    tags: ['Marketing', 'Management', 'International'],
    link: '',
    savedDate: '2024-01-15'
  },
  {
    id: 3,
    title: 'UX Designer',
    company: 'Creative Studios',
    location: 'Remote',
    description: 'Remote UX Designer position for international projects. Flexible hours, competitive hourly rate ($80-120), exciting clients. Must have portfolio and 3+ years experience.',
    tags: ['UX Design', 'Remote', 'Portfolio'],
    link: 'https://creativestudios.com/ux-position',
    savedDate: '2024-01-14'
  },
  {
    id: 4,
    title: 'Product Manager',
    company: 'Innovation Labs',
    location: 'Hamburg, Germany',
    description: 'Lead product development for our international markets. Experience with agile methodologies required. Competitive salary and relocation assistance available.',
    tags: ['Product Management', 'Agile', 'International'],
    link: 'https://innovationlabs.com/careers',
    savedDate: '2024-01-13'
  },
  {
    id: 5,
    title: 'Data Scientist',
    company: 'Analytics Corp',
    location: 'Munich, Germany',
    description: 'Join our data team to build machine learning models for customer insights and business optimization. Experience with Python, R, and SQL required.',
    tags: ['Data Science', 'Machine Learning', 'Python'],
    link: 'https://analyticscorp.com/careers',
    savedDate: '2024-01-12'
  },
  {
    id: 6,
    title: 'Frontend Developer',
    company: 'Digital Solutions',
    location: 'Cologne, Germany',
    description: 'Frontend developer position focusing on modern web applications. Experience with Vue.js, TypeScript, and responsive design required.',
    tags: ['Frontend', 'Vue.js', 'TypeScript'],
    link: '',
    savedDate: '2024-01-11'
  }
]

// Computed
const filteredSavedJobs = computed(() => {
  let filtered = [...savedJobs.value]

  if (filters.value.search) {
    const searchTerm = filters.value.search.toLowerCase()
    filtered = filtered.filter(job =>
      job.title.toLowerCase().includes(searchTerm) ||
      job.company.toLowerCase().includes(searchTerm) ||
      job.location.toLowerCase().includes(searchTerm) ||
      job.description.toLowerCase().includes(searchTerm)
    )
  }

  if (filters.value.dateRange) {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (filters.value.dateRange) {
      case 'today':
        filtered = filtered.filter(job => {
          const savedDate = new Date(job.savedDate)
          return savedDate >= today
        })
        break
      case 'week': {
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
        filtered = filtered.filter(job => {
          const savedDate = new Date(job.savedDate)
          return savedDate >= weekAgo
        })
        break
      }
      case 'month': {
        const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
        filtered = filtered.filter(job => {
          const savedDate = new Date(job.savedDate)
          return savedDate >= monthAgo
        })
        break
      }
      case 'quarter': {
        const quarterAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())
        filtered = filtered.filter(job => {
          const savedDate = new Date(job.savedDate)
          return savedDate >= quarterAgo
        })
        break
      }
    }
  }

  return filtered
})

const totalPages = computed(() => Math.ceil(filteredSavedJobs.value.length / itemsPerPage.value))

// Methods
const fetchSavedJobs = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    // In a real app, this would make an API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    savedJobs.value = sampleSavedJobs
    totalItems.value = sampleSavedJobs.length
  } catch (error) {
    errorMessage.value = 'Failed to fetch saved jobs. Please try again.'
    console.error('Error fetching saved jobs:', error)
  } finally {
    isLoading.value = false
  }
}

const applyFilters = () => {
  currentPage.value = 1
  // In a real app, this would trigger an API call with the new filters
}

const removeSavedJob = (jobId) => {
  if (confirm('Remove this job from saved jobs?')) {
    // In a real app, this would make an API call to remove the saved job
    savedJobs.value = savedJobs.value.filter(job => job.id !== jobId)
    selectedJobs.value = selectedJobs.value.filter(id => id !== jobId)
    totalItems.value--
  }
}

const removeSelectedJobs = () => {
  if (confirm(`Remove ${selectedJobs.value.length} selected job${selectedJobs.value.length === 1 ? '' : 's'} from saved jobs?`)) {
    // In a real app, this would make an API call to remove the selected saved jobs
    savedJobs.value = savedJobs.value.filter(job => !selectedJobs.value.includes(job.id))
    totalItems.value -= selectedJobs.value.length
    selectedJobs.value = []
  }
}

const clearSelection = () => {
  selectedJobs.value = []
}

const viewJob = (jobId) => {
  router.push(`/jobs/${jobId}`)
}

const openJobLink = (link) => {
  if (link) {
    window.open(link, '_blank')
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  fetchSavedJobs()
})
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
